import { Chunk, Schema } from 'effect';

export type PaginationItem = {
  type: 'page' | 'ellipsis';
  page?: number;
};

/**
 * Creates a pagination item for a page number
 */
const createPageItem = (page: number): PaginationItem => ({
  type: 'page',
  page,
});

/**
 * Creates an ellipsis item
 */
const createEllipsisItem = (): PaginationItem => ({ type: 'ellipsis' });

/**
 * Determines the middle range boundaries based on current page and total pages
 */
const calculateMiddleRange = (currentPage: number, lastPage: number) => {
  if (currentPage <= 4) {
    return { startPage: 2, endPage: 5 };
  }

  if (currentPage >= lastPage - 3) {
    return { startPage: lastPage - 4, endPage: lastPage - 1 };
  }

  return { startPage: currentPage - 2, endPage: currentPage + 2 };
};

/**
 * Generates pagination items for display using functional programming patterns
 * Always shows up to 7 items when possible, with smart ellipsis placement
 *
 * @param currentPage - Current active page
 * @param lastPage - Total number of pages
 * @returns Array of pagination items to render
 *
 * @example
 * ```typescript
 * generatePaginationRange(6, 25)
 * // Returns: [1, '...', 4, 5, 6, 7, 8, '...', 25]
 *
 * generatePaginationRange(3, 5)
 * // Returns: [1, 2, 3, 4, 5]
 *
 * generatePaginationRange(23, 25)
 * // Returns: [1, '...', 21, 22, 23, 24, 25]
 * ```
 */
export function generatePaginationRange(currentPage: number, lastPage: number) {
  // If we have 7 or fewer pages, show them all
  if (lastPage <= 7) {
    return Chunk.range(1, lastPage).pipe(Chunk.map(createPageItem), Chunk.toArray);
  }

  const { startPage, endPage } = calculateMiddleRange(currentPage, lastPage);

  return Chunk.empty<PaginationItem>().pipe(
    // Always add first page
    Chunk.append(createPageItem(1)),

    // Add ellipsis before middle range if needed
    Chunk.appendAll(startPage > 2 ? Chunk.of(createEllipsisItem()) : Chunk.empty()),

    // Add middle range pages
    Chunk.appendAll(Chunk.range(startPage, endPage).pipe(Chunk.map(createPageItem))),

    // Add ellipsis after middle range if needed
    Chunk.appendAll(endPage < lastPage - 1 ? Chunk.of(createEllipsisItem()) : Chunk.empty()),

    // Always add last page (if it's not already included)
    Chunk.appendAll(lastPage > 1 ? Chunk.of(createPageItem(lastPage)) : Chunk.empty()),

    Chunk.toReadonlyArray
  );
}

/**
 * One page of a listing, as a capability port answers it.
 *
 * Every listing operation in the engine's capability tier returns this shape.
 * It carries the items the caller asked for and the three numbers a pager
 * needs: how many items exist in total, which page this is, and how large a
 * page is.
 *
 * The envelope a backend wraps its collection in — Hydra's `hydra:member` and
 * `hydra:totalItems`, a `data`/`meta` pair, a `Link` header — never reaches a
 * loader. The tenant adapter reads its own envelope and answers this one, so
 * two backends that page differently still page the same way here.
 *
 * `total` is the count of what the listing pages through, not a rollup of a
 * category's descendants. Those are different numbers and a taxon reports both.
 */
export type Page<A> = {
  readonly items: ReadonlyArray<A>;
  readonly total: number;
  readonly page: number;
  readonly itemsPerPage: number;
  /** The source's last-page fact, when its response supplies one. */
  readonly lastPage?: number | undefined;
};

/**
 * What a caller asks a listing for: which page, and how large.
 *
 * Neutral by construction — a page number and a page size, with no backend
 * filter syntax. A capability that filters its listing extends this with the
 * filter's own neutral parameter, as `CategoryListing` does with `taxonCode`.
 */
export type PageRequest = {
  readonly page: number;
  readonly itemsPerPage: number;
};

/**
 * The {@link Page} envelope as a schema, over the schema of one item.
 *
 * `Page` itself is a type because a listing port answers it and nothing decodes
 * it on the wire — the tenant adapter reads its own envelope and constructs
 * this one. What does need a schema is a test that checks an adapter kept its
 * promise, which is what `capabilityContract` uses this for.
 */
export const PageOf = <S extends Schema.Top>(item: S) =>
  Schema.Struct({
    items: Schema.Array(item),
    total: Schema.Finite.check(Schema.isInt()),
    page: Schema.Finite.check(Schema.isInt()),
    itemsPerPage: Schema.Finite.check(Schema.isInt()),
    lastPage: Schema.optional(Schema.Finite.check(Schema.isInt())),
  });
